import { doorRules, measurementGuides, type MeasurementMode } from '@planner/config';
import { useNavigate, useRouter } from '@tanstack/react-router';
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { track } from '../lib/analytics/index.js';
import { completeStep } from '../lib/analytics/planner.js';
import type { NumericField } from '../lib/analytics/events.js';
import { measurementModeIds, measurementPresentation } from '../../appdata.js';
import { DepthClearanceHelp } from '../components/DepthClearanceHelp.js';
import { GuideDownload } from '../components/GuideDownload.js';
import { InfoTip } from '../components/InfoTip.js';
import { StepNavigation, StepProgress } from '../components/StepProgress.js';
import { ZoomableDiagram } from '../components/ZoomableDiagram.js';
import type { MeasurementField } from '../lib/measurements.js';
import { useMeasurementsReadiness } from '../lib/useMeasurements.js';
import { useNumberInput } from '../lib/useNumberInput.js';
import {
  useDoorStore,
  hasAcknowledgement,
  type DiyInputs,
  type TradeInputs,
  type AcknowledgementKind,
} from '../stores/doorStore.js';

type DimensionMarker = 'W' | 'H' | 'D';

const openingFields: {
  field: keyof DiyInputs;
  label: string;
  name: string;
  marker: DimensionMarker;
  fullWidth?: boolean;
}[] = [
  { field: 'widthTop', label: 'Width (Top)', name: 'measure-width-top', marker: 'W' },
  { field: 'widthMiddle', label: 'Width (Middle)', name: 'measure-width-middle', marker: 'W' },
  {
    field: 'widthBottom',
    label: 'Width (Bottom)',
    name: 'measure-width-bottom',
    marker: 'W',
    fullWidth: true,
  },
  { field: 'height', label: 'Height (Left)', name: 'measure-height-left', marker: 'H' },
  { field: 'heightRight', label: 'Height (Right)', name: 'measure-height-right', marker: 'H' },
];

const exactFields: {
  field: keyof TradeInputs;
  validation: MeasurementField;
  label: string;
  name: string;
  marker: DimensionMarker;
}[] = [
  {
    field: 'width',
    validation: 'doorWidth',
    label: 'Door Width',
    name: 'measure-door-width',
    marker: 'W',
  },
  {
    field: 'height',
    validation: 'doorHeight',
    label: 'Door Height',
    name: 'measure-door-height',
    marker: 'H',
  },
];

function CheckIcon({ className }: { className: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function ModeIcon({ mode }: { mode: MeasurementMode }) {
  return (
    <svg
      aria-hidden="true"
      className="size-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {mode === 'diy' ? (
        <path d="M3 12l2-2m0 0 7-7 7 7M5 10v10a1 1 0 0 0 1 1h3m10-11 2 2m-2-2v10a1 1 0 0 1-1 1h-3m-6 0a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1m-6 0h6" />
      ) : (
        <>
          <rect x="5" y="2" width="14" height="20" rx="1" />
          <circle cx="14" cy="12" r="1" fill="currentColor" stroke="none" />
        </>
      )}
    </svg>
  );
}

function MeasurementInput({
  name,
  label,
  marker,
  value,
  onChange,
  onBlur,
  error,
  fullWidth = false,
  hint,
  labelHelp,
}: {
  name: string;
  label: string;
  marker: DimensionMarker;
  value: string;
  onChange(value: string): void;
  onBlur(): void;
  error: 'required' | 'invalid' | undefined;
  fullWidth?: boolean;
  hint?: string;
  labelHelp?: ReactNode;
}) {
  const { badInput, cannotDisplay, transferNotice, inputProps } = useNumberInput(value, onChange);
  const markerColor = { W: 'bg-blue-600', H: 'bg-brand', D: 'bg-gray-600' }[marker];
  const fieldError = badInput && error === 'required' ? 'invalid' : error;
  const showError = cannotDisplay || Boolean(fieldError);

  return (
    <div className={`min-w-0 ${fullWidth ? 'col-span-2' : ''}`}>
      <div className="flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className={`inline-flex size-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white ${markerColor}`}
        >
          {marker}
        </span>
        <label htmlFor={name} className="text-xs text-gray-600">
          {label}
        </label>
        {labelHelp}
      </div>
      <div className="relative mt-1">
        <input
          id={name}
          data-name={name}
          type="number"
          min="1"
          step="1"
          inputMode="numeric"
          {...inputProps}
          onBlur={onBlur}
          aria-invalid={showError ? true : undefined}
          aria-describedby={`${name}-unit${hint ? ` ${name}-hint` : ''}${showError ? ` ${name}-error` : ''}${transferNotice ? ` ${name}-transfer-notice` : ''}`}
          className="min-h-12 w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 pr-12 text-base focus-visible:outline-2 focus-visible:outline-brand sm:text-sm"
        />
        <span
          id={`${name}-unit`}
          className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-gray-400"
        >
          mm
        </span>
      </div>
      {hint && (
        <p id={`${name}-hint`} className="mt-1 text-[11px] text-gray-400">
          {hint}
        </p>
      )}
      {showError && (
        <p id={`${name}-error`} className="mt-1 text-sm text-red-700">
          {cannotDisplay ? (
            <>
              Your saved entry{' '}
              <span className="break-all whitespace-pre-wrap">&ldquo;{value}&rdquo;</span> cannot be
              shown in a number field. Enter a positive whole number of millimetres.
            </>
          ) : fieldError === 'required' ? (
            'Enter this measurement.'
          ) : (
            'Enter a positive whole number of millimetres.'
          )}
        </p>
      )}
      {transferNotice && (
        <p id={`${name}-transfer-notice`} role="status" className="mt-1 text-sm text-red-700">
          Paste or drop digits only. Select the whole measurement to replace it.
        </p>
      )}
    </div>
  );
}

function MeasurementNotice({
  name,
  control,
  title,
  acknowledged,
  onAcknowledge,
  onReview,
  note,
  children,
}: {
  name: string;
  control: string;
  title: string;
  acknowledged: boolean;
  onAcknowledge(): void;
  onReview(): void;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      data-name={name}
      className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-white p-4"
    >
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-500"
      >
        <svg
          className="size-[15px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m10.3 3.9-8.1 14a2 2 0 0 0 1.7 3h16.2a2 2 0 0 0 1.7-3l-8.1-14a2 2 0 0 0-3.4 0Z" />
          <path d="M12 9v4m0 4h.01" />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="mb-1 text-sm font-semibold text-gray-900">{title}</h3>
        <p className="mb-2 text-sm text-gray-600">{children}</p>
        {note && <p className="mb-3 text-xs text-gray-500">{note}</p>}
        {acknowledged ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-700">
            <span data-name={control} role="status" className="flex items-center gap-1.5">
              <CheckIcon className="size-3 text-green-600" />
              Acknowledged &mdash; ready to continue
            </span>
            <button
              type="button"
              data-name={`${control}-review`}
              onClick={onReview}
              className="min-h-11 px-1 font-semibold text-progress underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-brand"
            >
              Review again
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-name={control}
            onClick={onAcknowledge}
            className="rounded-lg bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            Continue &mdash; I understand
          </button>
        )}
      </div>
    </section>
  );
}

export function MeasurementsStep() {
  const { draft, evaluation, retry } = useMeasurementsReadiness();
  const { measurements } = draft;
  const { mode } = measurements;
  const selectedMode = mode === null ? null : measurementPresentation[mode];
  const navigate = useNavigate();
  const router = useRouter();
  const invalidated = useRef(new Set<AcknowledgementKind>());
  const [touched, setTouched] = useState<Partial<Record<MeasurementField, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const selectMode = useDoorStore((state) => state.selectMeasurementMode);
  const setDiy = useDoorStore((state) => state.setDiyMeasurement);
  const setTrade = useDoorStore((state) => state.setTradeMeasurement);
  const setDepth = useDoorStore((state) => state.setDepth);
  const acknowledge = useDoorStore((state) => state.acknowledge);
  const clearAcknowledgement = useDoorStore((state) => state.clearAcknowledgement);
  const guardNotice = useDoorStore((state) => state.guardNotice);
  const setGuardNotice = useDoorStore((state) => state.setGuardNotice);
  useEffect(() => {
    if (
      (guardNotice === 'measurements' && evaluation.readyExceptCatalogue) ||
      (guardNotice === 'catalogue' && evaluation.canContinue)
    ) {
      setGuardNotice(null);
    }
  }, [evaluation.canContinue, evaluation.readyExceptCatalogue, guardNotice, setGuardNotice]);
  const fieldError = (field: MeasurementField) =>
    touched[field] || submitted ? evaluation.errors[field] : undefined;
  const fieldIds: Record<MeasurementField, NumericField> = {
    widthTop: 'width_top',
    widthMiddle: 'width_middle',
    widthBottom: 'width_bottom',
    height: 'height_left',
    heightRight: 'height_right',
    doorWidth: 'door_width',
    doorHeight: 'door_height',
    depth: 'depth',
  };
  const touch = (field: MeasurementField) => {
    setTouched((previous) => ({ ...previous, [field]: true }));
    track('configuration_changed', { field: fieldIds[field] });
    for (const kind of invalidated.current) {
      track('configuration_changed', { field: `${kind}_acknowledgement` });
    }
    invalidated.current.clear();
  };
  const editMeasurement = (edit: () => void) => {
    const before = useDoorStore.getState().draft.measurements;
    edit();
    const after = useDoorStore.getState().draft.measurements;
    for (const kind of ['width', 'height', 'depth'] as const) {
      if (hasAcknowledgement(before, kind) && !hasAcknowledgement(after, kind)) {
        invalidated.current.add(kind);
      }
    }
  };
  const changeAcknowledgement = (kind: AcknowledgementKind, checked: boolean) => {
    if (hasAcknowledgement(useDoorStore.getState().draft.measurements, kind) === checked) return;
    if (checked) acknowledge(kind);
    else clearAcknowledgement(kind);
    track('configuration_changed', { field: `${kind}_acknowledgement` });
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    if (evaluation.canContinue) {
      void completeStep(
        'measure',
        () => navigate({ to: '/planner/lock' }),
        () => router.state.location.pathname === '/planner/lock',
      );
    }
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (
      event.key === 'Enter' &&
      !event.nativeEvent.isComposing &&
      event.target instanceof HTMLInputElement &&
      event.target.type === 'number' &&
      !evaluation.canContinue
    ) {
      event.preventDefault();
      setSubmitted(true);
    }
  };
  const offset = (value: number, allowance: number) => String(BigInt(value) + BigInt(allowance));

  return (
    <>
      <StepProgress currentIndex={1} />
      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-8 sm:py-10">
        <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
          Enter your measurements
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          Choose how you&apos;d like to measure, then enter your dimensions below. All measurements
          are in millimetres (mm).
        </p>
        <form onSubmit={submit} onKeyDown={handleKeyDown} noValidate>
          <fieldset className="mt-7 grid grid-cols-2 gap-3 sm:gap-4">
            <legend className="sr-only">Choose how to measure</legend>
            {measurementModeIds.map((id) => (
              <label
                key={id}
                className={`relative flex min-w-0 cursor-pointer flex-col items-center gap-2.5 rounded-xl border-2 px-3 py-5 text-center has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand ${
                  mode === id ? 'border-brand bg-page shadow-sm' : 'border-gray-200 bg-white'
                }`}
              >
                <input
                  type="radio"
                  name="measurement-mode"
                  data-name={`measure-mode-${id}`}
                  value={id}
                  checked={mode === id}
                  onChange={() => {
                    if (mode !== id) {
                      selectMode(id);
                      track('configuration_changed', { field: 'measurement_mode', value: id });
                    }
                  }}
                  className="sr-only"
                />
                {mode === id && (
                  <span
                    data-name={`measure-mode-selected-${id}`}
                    aria-hidden="true"
                    className="absolute top-2.5 right-2.5 flex size-4 items-center justify-center rounded-full bg-brand text-white"
                  >
                    <CheckIcon className="size-2.5" />
                  </span>
                )}
                <span
                  data-name={`measure-mode-icon-${id}`}
                  aria-hidden="true"
                  className={`flex size-10 items-center justify-center rounded-xl ${
                    mode === id ? 'bg-brand-soft text-progress' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  <ModeIcon mode={id} />
                </span>
                <span>
                  <span
                    className={`block text-xs leading-tight font-semibold sm:text-sm ${
                      mode === id ? 'text-gray-900' : 'text-gray-700'
                    }`}
                  >
                    {measurementPresentation[id].title}
                  </span>
                  <span className="mt-0.5 hidden text-xs text-gray-500 sm:block">
                    {measurementPresentation[id].description}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          {submitted && Object.keys(evaluation.errors).length > 0 && (
            <p role="alert" className="mt-4 text-sm text-red-700">
              Please check the measurement errors.
            </p>
          )}
          {mode && selectedMode && (
            <section
              className={`mt-6 grid gap-6 rounded-xl border border-gray-200 bg-white p-5 sm:p-6 ${mode === 'diy' ? 'lg:grid-cols-2' : ''}`}
            >
              <div className="text-center">
                <h2 className="text-[10px] font-bold tracking-widest text-gray-700 uppercase">
                  {selectedMode.heading}
                </h2>
                <p className="mt-0.5 text-[9px] text-gray-400">{selectedMode.instructions}</p>
                <ZoomableDiagram
                  topic={mode === 'diy' ? 'measurement_diy' : 'measurement_trade'}
                  src={selectedMode.image.src}
                  alt={selectedMode.image.alt}
                  title={selectedMode.image.title}
                  className={
                    mode === 'diy'
                      ? 'max-w-[200px] md:max-w-60 lg:max-w-[260px]'
                      : 'max-w-[170px] md:max-w-52'
                  }
                />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-800">
                  {selectedMode.fieldsHeading}
                </h2>
                <p className="mt-1 text-[11px] text-gray-400">
                  Available range:{' '}
                  {evaluation.limits ? (
                    <>
                      {mode === 'diy'
                        ? `Smallest opening width: ${offset(evaluation.limits.minWidth, doorRules.clearance.width)}-${offset(evaluation.limits.maxWidth, doorRules.clearance.width)} mm; shortest opening height: ${offset(evaluation.limits.minHeight, doorRules.clearance.height)}-${offset(evaluation.limits.maxHeight, doorRules.clearance.height)} mm.`
                        : `Finished width: ${evaluation.limits.minWidth}-${evaluation.limits.maxWidth} mm; height: ${evaluation.limits.minHeight}-${evaluation.limits.maxHeight} mm.`}{' '}
                      Actual width and height combinations must match an available size.
                    </>
                  ) : !draft.doorType ? (
                    'Choose a door type to see available sizes.'
                  ) : evaluation.availability.status === 'loading' ? (
                    'Checking the catalogue...'
                  ) : evaluation.availability.status === 'empty' ? (
                    'No active sizes are available for this door type.'
                  ) : (
                    'Unavailable until the catalogue can be checked.'
                  )}
                </p>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {mode === 'diy'
                    ? openingFields.map(({ field, label, name, marker, fullWidth }) => (
                        <MeasurementInput
                          key={field}
                          name={name}
                          label={label}
                          marker={marker}
                          fullWidth={fullWidth}
                          value={measurements.diy[field]}
                          onChange={(value) => editMeasurement(() => setDiy(field, value))}
                          onBlur={() => touch(field)}
                          error={fieldError(field)}
                        />
                      ))
                    : exactFields.map(({ field, validation, label, name, marker }) => (
                        <MeasurementInput
                          key={field}
                          name={name}
                          label={label}
                          marker={marker}
                          value={measurements.trade[field]}
                          onChange={(value) => editMeasurement(() => setTrade(field, value))}
                          onBlur={() => touch(validation)}
                          error={fieldError(validation)}
                        />
                      ))}
                  <MeasurementInput
                    name="measure-depth"
                    label="Depth clearance"
                    marker="D"
                    fullWidth
                    hint={`Recommended ${doorRules.depthClearance.recommended} mm or more`}
                    labelHelp={<DepthClearanceHelp />}
                    value={measurements.depth}
                    onChange={(value) => editMeasurement(() => setDepth(value))}
                    onBlur={() => touch('depth')}
                    error={fieldError('depth')}
                  />
                </div>
                <p className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                  {mode === 'diy' ? (
                    <>
                      <strong className="text-gray-700">We handle the fit.</strong> We automatically
                      deduct the right clearances &mdash; you just measure the opening.
                    </>
                  ) : (
                    <>
                      <strong className="text-gray-700">No adjustments made.</strong> We manufacture
                      to these exact dimensions.
                    </>
                  )}
                </p>
              </div>
            </section>
          )}
          {evaluation.size && (
            <section
              data-name="manufactured-size"
              className="mt-4 flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4"
            >
              {evaluation.canContinue && (
                <span
                  aria-hidden="true"
                  className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand text-white"
                >
                  <CheckIcon className="size-4" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">
                    {mode === 'trade'
                      ? evaluation.canContinue
                        ? 'Door size confirmed'
                        : 'Door size entered'
                      : 'Calculated manufactured size'}
                  </h3>
                  {evaluation.canContinue && (
                    <InfoTip label="Why this size?">
                      Based on the measurements you provided, our calculator has determined the
                      optimal door size for manufacture to be {evaluation.size.width}mm wide &times;{' '}
                      {evaluation.size.height}mm high. This allows the necessary clearance for the
                      hinges and locking mechanism, ensuring the door operates correctly.
                    </InfoTip>
                  )}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs tracking-wide text-gray-500 uppercase">Height</p>
                    <p className="text-2xl font-bold">{evaluation.size.height} mm</p>
                  </div>
                  <div>
                    <p className="text-xs tracking-wide text-gray-500 uppercase">Width</p>
                    <p className="text-2xl font-bold">{evaluation.size.width} mm</p>
                  </div>
                </div>
              </div>
            </section>
          )}
          {evaluation.widthWarning && (
            <MeasurementNotice
              name="out-of-square-notice"
              control="out-of-square-ack"
              title="Your opening may not be square"
              acknowledged={hasAcknowledgement(measurements, 'width')}
              onAcknowledge={() => changeAcknowledgement('width', true)}
              onReview={() => changeAcknowledgement('width', false)}
              note={
                <>
                  While small variances (under {doorRules.outOfSquareToleranceMm}mm) could work,
                  installing a {draft.doorType === 'security' ? 'security door' : 'screen door'}{' '}
                  with higher variances in width is not recommended. We recommend you contact a
                  tradesman to make your opening square and then remeasuring to ensure the best fit.
                  If you proceed, you do so at your own risk.
                </>
              }
            >
              Your three width measurements differ by {evaluation.widthDifference}mm. We&apos;ll
              manufacture to the narrowest point so that the door fits into the opening.
            </MeasurementNotice>
          )}
          {evaluation.heightWarning && (
            <MeasurementNotice
              name="height-spread-notice"
              control="height-spread-ack"
              title="Your opening heights differ"
              acknowledged={hasAcknowledgement(measurements, 'height')}
              onAcknowledge={() => changeAcknowledgement('height', true)}
              onReview={() => changeAcknowledgement('height', false)}
            >
              The heights differ by {evaluation.heightDifference} mm. We use the shortest height, so
              gaps around the door may be uneven.
            </MeasurementNotice>
          )}
          {evaluation.depthStatus === 'insufficient' && (
            <section
              data-name="depth-notice"
              className="mt-4 rounded-lg border border-red-300 bg-red-50 p-4"
            >
              <h3 className="text-sm font-semibold">Not enough depth clearance</h3>
              <p className="mt-1 text-sm">
                At least {doorRules.depthClearance.min} mm is required for the existing handle or
                other fitting.
              </p>
            </section>
          )}
          {evaluation.depthStatus === 'reduced' && (
            <MeasurementNotice
              name="depth-notice"
              control="depth-ack"
              title="Reduced depth clearance"
              acknowledged={hasAcknowledgement(measurements, 'depth')}
              onAcknowledge={() => changeAcknowledgement('depth', true)}
              onReview={() => changeAcknowledgement('depth', false)}
            >
              This may still work, but {doorRules.depthClearance.recommended} mm or more is
              recommended. Check that the existing handle or other fitting will clear the screen
              door.
            </MeasurementNotice>
          )}
          {evaluation.reasons.length > 0 && (
            <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 text-sm">
              <p className="font-semibold">Before continuing:</p>
              <ul className="mt-1 list-disc pl-5">
                {evaluation.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
              {(evaluation.availability.status === 'error' ||
                evaluation.availability.status === 'offline' ||
                evaluation.availability.status === 'empty') && (
                <button
                  type="button"
                  data-name="measure-catalogue-retry"
                  onClick={retry}
                  className="mt-2 font-semibold text-brand-strong underline focus-visible:outline-2 focus-visible:outline-brand"
                >
                  Retry catalogue
                </button>
              )}
            </div>
          )}
          {mode && selectedMode && (
            <details data-name="measure-help" className="mt-5">
              <summary
                data-name="measure-help-toggle"
                className="inline-flex min-h-11 cursor-pointer list-none items-center gap-2 text-xs text-gray-400 hover:text-gray-600 focus-visible:outline-2 focus-visible:outline-brand"
              >
                <svg
                  aria-hidden="true"
                  className="size-4 shrink-0 text-brand"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 11v6" strokeLinecap="round" />
                  <circle cx="12" cy="7.5" r=".5" fill="currentColor" stroke="none" />
                </svg>
                {selectedMode.helpLabel}
              </summary>
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <GuideDownload key={mode} mode={mode} guide={measurementGuides[mode]} />
              </div>
            </details>
          )}
          <StepNavigation currentIndex={1} canContinue={evaluation.canContinue} submit />
        </form>
      </main>
    </>
  );
}
